import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleHealthcareCosts() {
  return (
    <ArticleLayout
      meta={{
        slug: "retirement-healthcare-costs",
        hub: "Healthcare & Medicare",
        time: "8 min read",
        title: "Retirement Healthcare Costs: What to Expect and How to Plan | Centennial",
        seoDescription:
          "A 65-year-old couple may need $300,000+ for healthcare in retirement. What Medicare covers (and doesn't), the pre-65 insurance gap, and how to build medical inflation into your plan free.",
        h1: "Retirement Healthcare Costs: What to Expect",
        hook: "Healthcare is the retirement expense people underestimate most — and the one that inflates faster than everything else in your budget.",
        quickAnswer:
          "Plan for healthcare as its own line item: a 65-year-old couple retiring today should expect roughly $300,000+ in lifetime out-of-pocket healthcare costs (premiums, copays, dental, vision, hearing) even with Medicare — and that excludes long-term care. Budget $6,000–$8,000 per person per year on Medicare, far more if you retire before 65, and assume medical costs inflate 1–2 points faster than general inflation.",
      }}
    >
      <H2>The big number, broken down</H2>
      <p>
        Widely cited estimates (Fidelity's annual retiree healthcare study and EBRI research) put
        lifetime healthcare costs for a 65-year-old couple at roughly $300,000–$350,000 in
        today's dollars. That's not one bill — it's decades of premiums, deductibles, and
        the things Medicare doesn't cover:
      </p>
      <DataTable
        head={["Cost", "Typical range (per person, 2026)"]}
        rows={[
          ["Medicare Part B premium", "~$2,200/yr (higher with IRMAA surcharges)"],
          ["Part D drug plan", "$400–$1,200/yr"],
          ["Medigap or Advantage out-of-pocket", "$1,500–$4,000/yr"],
          ["Dental, vision, hearing (mostly uncovered)", "$1,000–$3,000/yr"],
          ["Total, typical year", "$6,000–$8,000/yr"],
        ]}
      />

      <H2>The pre-65 gap is the budget killer</H2>
      <p>
        Medicare starts at 65. Retire at 60 and you need five years of private coverage — ACA
        marketplace plans for a 60-year-old commonly run $800–$1,400/month before subsidies.
        That's $50,000–$85,000 per person before Medicare ever begins, and it's the single
        biggest reason "retire early with $500k" plans fail. (Related:{" "}
        <Link className="text-[#2B5339] underline" to="/learn/retire-with-500k">Can I retire with $500,000?</Link>)
      </p>

      <H2>Medical inflation compounds against you</H2>
      <p>
        Medical care costs have historically risen faster than general inflation (US Bureau of
        Labor Statistics CPI data). Over a 30-year retirement, a 1.5-point gap compounds
        brutally: a $7,000 annual healthcare budget at 65 becomes $11,000+ in real terms by 95.
        Any honest projection must inflate healthcare separately — most calculators don't.
      </p>

      <H2>Long-term care: the unmodeled risk</H2>
      <p>
        Roughly half of 65-year-olds will need some paid long-term care. A private nursing-home
        room now exceeds $110,000/year nationally; home health aides run $30+/hour. Options:
        long-term-care insurance (buy in your 50s), hybrid life/LTC policies, self-insuring with
        a dedicated bucket, or Medicaid as the backstop. The wrong answer is assuming it won't
        happen.
      </p>

      <H2>Build it into your plan — free</H2>
      <p>
        The Centennial calculator has a dedicated healthcare module: set your annual healthcare
        spend and a separate medical-inflation rate, and it projects the cost curve to age 100
        inside your Monte Carlo plan. Pair it with the{" "}
        <Link className="text-[#2B5339] underline" to="/learn/readiness-checklist">readiness checklist</Link>{" "}
        (healthcare section included) and keep medications organized with the free{" "}
        <Link className="text-[#2B5339] underline" to="/medication-safety">medication safety card</Link>.
      </p>
    </ArticleLayout>
  );
}
